import React, { useEffect, useState, Fragment, useRef } from 'react';
import { useSelector } from 'react-redux';
import Loader from 'components/layout/Loader';
// import Spinner from 'components/Spinner';
import { intlModule } from '@chedri/base';
import DatePicker from 'components/forms/DatePicker';
import { Row, Col, Button } from 'react-bootstrap';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import axios from 'axios';
import moment from 'moment';
import ListFilterClient from 'components/layout/list/filters/ListFilterClient';
import ListFilterCompany from 'components/layout/list/filters/ListFilterCompany';

const CustomizedAxisTick = ({ x, y, stroke, payload }) => (
  <g transform={`translate(${x},${y})`}>
    <text x={0} y={-10} dy={16} textAnchor="end" fill="#666" transform="rotate(-90)">
      {payload.value}
    </text>
  </g>
);

const OrderTotals = () => {
  const messages = useSelector(intlModule.selectors.getMessages);
  const { widget_order_stats: messagesWos } = messages;
  const { title1 } = messagesWos;
  const [countOrders, setCountOrders] = useState(null);
  const [sumOrders, setSumOrders] = useState(null);
  const [dailyStats, setDailyStats] = useState([]);
  const [dateA, setDateA] = useState(moment().startOf('month'));
  const [dateB, setDateB] = useState(new Date());
  const [loadedStartDate, setLoadedStartDate] = useState(null);
  const [loadedEndDate, setLoadedEndDate] = useState(null);
  const [filterCompanies, setFilterCompanies] = useState([]);
  const [filterClients, setFilterClients] = useState([]);
  const [abortController, setAbortController] = useState(null);
  const appIntl = useSelector(({ intl }) => intl);
  const user = useSelector(({ login: { user } }) => user);
  const timezone =
    Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone ?? user?.profile?.timezone ?? 'Europe/Berlin';

  const [dateFrom, dateTo] = dateA < dateB ? [dateA, dateB] : [dateB, dateA];

  const listFilterCompaniesRef = useRef();
  const listFilterClientsRef = useRef();

  const saveCompanyRef = ref => {
    console.log('saveCompanyRef');
    listFilterCompaniesRef.current = ref;
  };
  const saveClientRef = ref => {
    console.log('saveClientRef');
    listFilterClientsRef.current = ref;
  };

  const colorCount = '#8884d8';
  const colorValue = '#82ca9d';

  const companiesUpdated = companiesDict => {
    const names = Object.keys(companiesDict).sort();
    setFilterCompanies(names);
    setFilterClients([]);
    // eslint-disable-next-line no-unused-expressions
    listFilterClientsRef?.current?.deselectAll?.(true);
    console.log('companiesUpdated', { current: listFilterClientsRef?.current });
  };

  const clientsUpdated = clientsDict => {
    const names = Object.keys(clientsDict).sort();
    setFilterCompanies([]);
    setFilterClients(names);
    // eslint-disable-next-line no-unused-expressions
    listFilterCompaniesRef?.current?.deselectAll?.(true);
    console.log('clientsUpdated', { current: listFilterCompaniesRef?.current });
  };

  useEffect(() => {
    const start = moment(dateFrom)
      .startOf('day')
      .format();
    const end = moment(dateTo)
      .endOf('day')
      .format();

    if (abortController) {
      // cancel previous request that is still running
      abortController.abort();
    }
    const controller = new AbortController();
    const { signal } = controller;
    setAbortController(controller);

    const params = { start, end };
    if (filterCompanies.length > 0) {
      params.companies = filterCompanies;
    } else if (filterClients.length > 0) {
      params.clients = filterClients;
    }

    axios
      .get('/api/orders/ordertotals/stats.json', { params, signal })
      .then(({ data }) => {
        const { count, value, daily_stats: dailyStats, start_date: startDate, end_date: endDate } = data;
        setCountOrders(count);
        setSumOrders(Number(value).toFixed(2));
        setDailyStats(dailyStats);
        setLoadedStartDate(new Date(startDate));
        setLoadedEndDate(new Date(endDate));
      })
      .catch(err => {
        console.error(err);
      })
      .finally(() => {
        setAbortController(null);
      });
  }, [dateFrom, dateTo, filterCompanies, filterClients]);

  const dateFormatter = new Intl.DateTimeFormat('de-DE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const chartData = dailyStats.map(dayStat => ({
    ...dayStat,
    date: dateFormatter.format(new Date(dayStat.date)),
  }));

  const isDatesLoaded = loadedStartDate && loadedEndDate;
  let subtitle = isDatesLoaded
    ? `${dateFormatter.format(loadedStartDate)} — ${dateFormatter.format(loadedEndDate)}`
    : messagesWos.loading;
  if (filterCompanies.length > 0) {
    subtitle += ` ${messagesWos.for_companies}: ${filterCompanies.join(', ')}`;
  } else if (filterClients.length > 0) {
    subtitle += ` ${messagesWos.for_clients}: ${filterClients.join(', ')}`;
  }

  const dateUpdate = (name, moment) => {
    console.log('dateUpdate', { name, moment, date: moment.toDate() });
    if (name === 'dateFrom') {
      setDateA(moment);
    } else if (name === 'dateTo') {
      setDateB(moment);
    } else {
      console.error('OrderTotals.js dateUpdate() Unknown name', { name });
    }
  };

  const quickButtons = () => {
    const lastNMonths = n => ({
      label: messagesWos[`last_${n}_months`],
      range: [
        moment()
          .startOf('month')
          .subtract(n, 'months'),
        moment(),
      ],
    });

    const times = [
      { label: messagesWos.this_month, range: [moment().startOf('month'), moment().endOf('day')] },
      {
        label: messagesWos.previous_month,
        range: [
          moment()
            .startOf('month')
            .subtract(1, 'months'),
          moment().startOf('month'),
        ],
      },
      lastNMonths(3),
      lastNMonths(6),
      lastNMonths(12),
    ];

    const setSelectedRange = ([dateA, dateB]) => () => {
      setDateA(dateA);
      setDateB(dateB);
    };

    return times.map(({ label, range }, i) => (
      <Fragment key={label}>
        <Button
          bsSize="small"
          bsStyle="info"
          onClick={setSelectedRange(range)}
          style={{ marginRight: i < times.length - 1 ? '5px' : '', marginBottom: '5px' }}
        >
          <i className="fas fa-calendar" /> {label}
        </Button>
      </Fragment>
    ));
  };

  const bigFigure = (title, value, color) => (
    <div style={{ textAlign: 'center', height: '100px', paddingTop: '25px', border: '1px solid #CCC' }}>
      <h4>
        {title}: <span style={{ color, fontSize: '1.6rem' }}>{value}</span>
      </h4>
    </div>
  );

  const noDataPlaceholder = text => {
    const style = {
      position: 'absolute',
      top: '0',
      left: '0',
      height: '350px',
      backgroundColor: '#EEE',
      textAlign: 'center',
      paddingTop: '170px',
      fontSize: '2em',
      color: '#CCC',
      opacity: 0.95,
      width: '100%',
    };

    return <div style={style}>{text}</div>;
  };

  return (
    <>
      <div className="widget-11-2 card no-border card-condensed widget-loader-circle full-height d-flex flex-column">
        <div className="padding-25">
          <h2 className="no-margin">
            {title1}
            {abortController && (
              <>
                {' '}
                <i className="fad fa-spinner-third fa-spin" />
              </>
            )}
          </h2>
          <p className="no-margin">{subtitle}</p>
        </div>
        <div className="padding-25">
          <Row style={{ marginBottom: '25px' }}>
            {loadedStartDate && loadedEndDate ? (
              <>
                <Col xs={12} md={6}>
                  {bigFigure(messagesWos.orders_count, countOrders, colorCount)}
                </Col>
                <Col xs={12} md={6}>
                  {bigFigure(messagesWos.orders_gross_total, `€${sumOrders}`, colorValue)}
                </Col>
              </>
            ) : (
              <Col xs={12}>
                <Loader />
              </Col>
            )}
          </Row>

          <Row>
            <Col xs={6} sm={3}>
              <DatePicker
                name="dateFrom"
                value={dateFrom}
                displayTz={timezone}
                returnTz={timezone}
                locale={appIntl.locale}
                onChange={dateUpdate}
                label={messages.date_from}
              />
            </Col>
            <Col xs={6} sm={3}>
              <DatePicker
                name="dateTo"
                value={dateTo}
                displayTz={timezone}
                returnTz={timezone}
                locale={appIntl.locale}
                onChange={dateUpdate}
                label={messages.date_to}
              />
            </Col>
            <Col xs={12} sm={6}>
              {quickButtons()}
            </Col>
            <Col xs={12} sm={3}>
              <ListFilterCompany updateLocation={false} onUpdate={companiesUpdated} saveRef={saveCompanyRef} />
            </Col>
            <Col xs={12} sm={3}>
              <ListFilterClient
                updateLocation={false}
                onUpdate={clientsUpdated}
                listName="clientUsers"
                saveRef={saveClientRef}
              />
            </Col>
          </Row>
          <div style={{ position: 'relative', height: '350px' }}>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart margin={{ top: 20, left: 10, right: 10, bottom: 30 }} data={chartData}>
                <Line
                  name={messagesWos.orders_count}
                  type="monotone"
                  dataKey="count"
                  yAxisId="left"
                  stroke={colorCount}
                  strokeWidth={2}
                />
                <Line
                  name={messagesWos.orders_gross_total}
                  type="monotone"
                  dataKey="value"
                  yAxisId="right"
                  stroke={colorValue}
                  strokeWidth={2}
                />
                <XAxis dataKey="date" tick={<CustomizedAxisTick />} height={100} />
                <YAxis
                  dataKey="count"
                  type="number"
                  yAxisId="left"
                  orientation="left"
                  label={{ value: messagesWos.orders_count, angle: -90, position: 'insideLeft' }}
                />
                <YAxis
                  dataKey="value"
                  type="number"
                  yAxisId="right"
                  unit="&euro;"
                  orientation="right"
                  label={{ value: messagesWos.orders_gross_total, angle: -90, position: 'insideRight' }}
                />
                <Tooltip />
                <Legend verticalAlign="top" height={36} />
              </LineChart>
            </ResponsiveContainer>
            {chartData.length === 0 && noDataPlaceholder(messagesWos.no_orders_in_date_range)}
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderTotals;
